import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { usePageTitle } from '@/hooks/usePageTitle'
import { LocalizedLink } from '@/components/LocalizedLink'
import {
  CtaBanner,
  PageHero,
  ProjectCategoryShowcase,
} from '@/components/ui'
import type { Project, ShowcaseItem } from '@/components/ui'

type CategoryKey = 'renovation' | 'exterior' | 'interior' | 'new'

interface SectionConfig {
  category: CategoryKey
  title: string
  description: string
}

function imagePath(folder: string, file: string) {
  return `/media/projects/${folder}/${encodeURIComponent(file)}`
}

function collectItems(projects: Project[], category: CategoryKey): ShowcaseItem[] {
  const categoryProjects = projects.filter((project) => project.category === category)

  return categoryProjects.flatMap((project): ShowcaseItem[] => {
    const pairs: ShowcaseItem[] = (project.beforeAfter ?? []).map(([before, after]) => ({
      type: 'pair' as const,
      before: imagePath(project.folder, before),
      after: imagePath(project.folder, after),
      title: project.title,
    }))

    if (project.id === 'complete') {
      const images: ShowcaseItem[] = project.images.map((file) => ({
        type: 'image' as const,
        src: imagePath(project.folder, file),
        alt: `${project.title} — ${file}`,
      }))
      return [...pairs, ...images]
    }

    return pairs.length > 0 ? pairs : project.images.map((file) => ({
      type: 'image' as const,
      src: imagePath(project.folder, file),
      alt: `${project.title} — ${file}`,
    }))
  })
}

export default function Projects() {
  const { t } = useTranslation()
  usePageTitle('projectsPage.meta.title', 'projectsPage.meta.description')

  const projects = t('projectsPage.projects', { returnObjects: true }) as Project[]
  const sections = t('projectsPage.sections', { returnObjects: true }) as SectionConfig[]

  return (
    <main id="main-content">
      <PageHero title={t('projectsPage.title')} subtitle={t('projectsPage.subtitle')} />

      {/* Sezioni per tipologia di lavorazione */}
      {sections.map((section, index) => (
        <ProjectCategoryShowcase
          key={section.category}
          index={index}
          title={section.title}
          description={section.description}
          items={collectItems(projects, section.category)}
          beforeLabel={t('projectsPage.beforeAfter.beforeLabel')}
          afterLabel={t('projectsPage.beforeAfter.afterLabel')}
          previousLabel={t('aria.previous', { defaultValue: 'Precedente' })}
          nextLabel={t('aria.next', { defaultValue: 'Successiva' })}
          goToLabel={t('aria.goTo', { defaultValue: 'Vai alla slide' })}
        />
      ))}

      <CtaBanner
        title={t('projectsPage.cta.title')}
        action={
          <LocalizedLink
            to="contacts"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-heading font-semibold rounded-lg hover:bg-bg transition-colors shadow-lg"
          >
            {t('buttons.contactUs')}
            <ArrowRight className="h-5 w-5" />
          </LocalizedLink>
        }
      />
    </main>
  )
}
